import React, { useRef, useLayoutEffect, useState, useEffect } from 'react'
import * as d3 from 'd3'
import { parseISO, format } from 'date-fns'
import { Box } from '@mui/material'

const TOOLTIP_WIDTH = 220

const EngagementScoreChart = ({ siteDayScores = [], regionwide = [] }) => {
  const svgRef = useRef()
  const containerRef = useRef()
  const [width, setWidth] = useState(800)

  useEffect(() => {
    if (containerRef.current) {
      setWidth(containerRef.current.offsetWidth)
    }
  }, [containerRef, siteDayScores])

  useLayoutEffect(() => {
    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    if (!siteDayScores.length) return

    const height = 320
    const margin = { top: 20, right: 140, bottom: 50, left: 55 }
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    svg.attr('width', width).attr('height', height)

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

    const allDates = [...new Set(siteDayScores.map((d) => d.date))].sort()
    const projectSeriesMap = {}

    siteDayScores.forEach((row) => {
      if (!projectSeriesMap[row.projectName]) projectSeriesMap[row.projectName] = {}
      projectSeriesMap[row.projectName][row.date] = row.siteScore
    })

    const series = Object.entries(projectSeriesMap).map(([name, values]) => ({
      name,
      values: allDates.map((date) => ({
        date,
        score: values[date] ?? null,
      })),
    }))

    const averageSeries = allDates.map((date) => {
      const scoresForDate = siteDayScores
        .filter((row) => row.date === date)
        .map((row) => row.siteScore)

      return {
        date,
        score: scoresForDate.length
          ? scoresForDate.reduce((sum, score) => sum + score, 0) / scoresForDate.length
          : null,
      }
    })

    const x = d3.scalePoint()
      .domain(allDates)
      .range([0, innerWidth])

    const y = d3.scaleLinear()
      .domain([0, 2])
      .range([innerHeight, 0])

    const colors = d3.scaleOrdinal(d3.schemeTableau10)
      .domain(series.map((s) => s.name))

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        d3.axisBottom(x)
          .tickValues(allDates.filter((_, i) => i % Math.max(1, Math.ceil(allDates.length / 8)) === 0))
          .tickFormat((d) => format(parseISO(d), 'dd MMM')),
      )
      .selectAll('text')
      .style('font-size', '10px')

    g.append('g')
      .call(d3.axisLeft(y).ticks(5).tickFormat((d) => `${(d / 2 * 100).toFixed(0)}%`))

    g.append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(y).ticks(5).tickSize(-innerWidth).tickFormat(''))
      .selectAll('line')
      .attr('stroke', '#e0e0e0')

    const line = d3.line()
      .defined((d) => d.score !== null)
      .x((d) => x(d.date))
      .y((d) => y(d.score))

    series.forEach((s) => {
      g.append('path')
        .datum(s.values)
        .attr('fill', 'none')
        .attr('stroke', colors(s.name))
        .attr('stroke-width', 2)
        .attr('d', line)
    })

    g.append('path')
      .datum(averageSeries)
      .attr('fill', 'none')
      .attr('stroke', '#111')
      .attr('stroke-width', 2.5)
      .attr('stroke-dasharray', '6,4')
      .attr('d', line)

    const hoverLine = g.append('line')
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', '#999')
      .attr('stroke-width', 1)
      .style('opacity', 0)

    const tooltip = g.append('g')
      .style('pointer-events', 'none')
      .style('opacity', 0)

    tooltip.append('rect')
      .attr('width', TOOLTIP_WIDTH)
      .attr('height', 20)
      .attr('rx', 6)
      .attr('fill', 'white')
      .attr('stroke', '#ccc')

    const tooltipTitle = tooltip.append('text')
      .attr('x', 10)
      .attr('y', 16)
      .style('font-size', '11px')
      .style('font-weight', 'bold')

    const tooltipBody = tooltip.append('g').attr('transform', 'translate(10, 32)')

    const updateTooltip = (date) => {
      const items = series
        .map((s) => ({
          name: s.name,
          color: colors(s.name),
          score: s.values.find((v) => v.date === date)?.score,
        }))
        .filter((item) => item.score !== null && item.score !== undefined)

      const avg = averageSeries.find((row) => row.date === date)?.score
      const rows = [
        ...items,
        ...(avg !== null && avg !== undefined ? [{ name: 'Average', color: '#111', score: avg, dashed: true }] : []),
      ]

      tooltipTitle.text(format(parseISO(date), 'dd MMM yyyy'))
      tooltipBody.selectAll('*').remove()

      rows.forEach((row, index) => {
        const yPos = index * 16
        tooltipBody.append('line')
          .attr('x1', 0)
          .attr('x2', 12)
          .attr('y1', yPos)
          .attr('y2', yPos)
          .attr('stroke', row.color)
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', row.dashed ? '6,4' : null)

        tooltipBody.append('text')
          .attr('x', 18)
          .attr('y', yPos + 4)
          .style('font-size', '11px')
          .text(`${row.name}: ${(row.score / 2 * 100).toFixed(0)}%`)
      })

      tooltip.select('rect')
        .attr('height', Math.max(28 + rows.length * 16, 44))
    }

    const overlay = g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'transparent')
      .on('mousemove', (event) => {
        const [mouseX] = d3.pointer(event)
        const nearestDate = allDates.reduce((closest, current) => {
          if (!closest) return current
          return Math.abs(x(current) - mouseX) < Math.abs(x(closest) - mouseX) ? current : closest
        }, null)

        if (!nearestDate) return

        const xPos = x(nearestDate)
        hoverLine
          .attr('x1', xPos)
          .attr('x2', xPos)
          .style('opacity', 1)

        updateTooltip(nearestDate)

        const tooltipX = xPos > innerWidth - TOOLTIP_WIDTH - 20 ? xPos - TOOLTIP_WIDTH - 12 : xPos + 12
        tooltip
          .attr('transform', `translate(${tooltipX}, 10)`)
          .style('opacity', 1)
      })
      .on('mouseleave', () => {
        hoverLine.style('opacity', 0)
        tooltip.style('opacity', 0)
      })

    const legend = g.append('g').attr('transform', `translate(${innerWidth + 16}, 0)`)

    series.slice(0, 10).forEach((s, index) => {
      const yPos = index * 18
      legend.append('line')
        .attr('x1', 0)
        .attr('x2', 16)
        .attr('y1', yPos)
        .attr('y2', yPos)
        .attr('stroke', colors(s.name))
        .attr('stroke-width', 2)

      legend.append('text')
        .attr('x', 22)
        .attr('y', yPos + 4)
        .style('font-size', '11px')
        .text(s.name)
    })

    const regionLegendY = Math.min(series.length, 10) * 18 + 8
    legend.append('line')
      .attr('x1', 0)
      .attr('x2', 16)
      .attr('y1', regionLegendY)
      .attr('y2', regionLegendY)
      .attr('stroke', '#111')
      .attr('stroke-width', 2.5)
      .attr('stroke-dasharray', '6,4')

    legend.append('text')
      .attr('x', 22)
      .attr('y', regionLegendY + 4)
      .style('font-size', '11px')
      .text('Average')
  }, [siteDayScores, regionwide, width])

  return (
    <Box ref={containerRef} sx={{ width: '100%', overflowX: 'auto' }}>
      <svg ref={svgRef} />
    </Box>
  )
}

export default EngagementScoreChart
